/**
 * This component is the collected Vendor Application.
 */
import {
  chain,
  get,
  includes,
  isEmpty,
  pick,
  reduce,
  size,
  startCase,
  mapValues,
} from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import { Prompt } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import { Feedback, If } from '@fashionnovateam/nova-ui-toolkit';

import styles from './InfluencerProfile.module.css';
import AddressFields from '../AddressFields/AddressFields';
import {
  makeAddressNames,
  makeAddressValues,
} from '../../Lib/Utils/FormUtils/address-fields';
import {
  makeBankNames,
  makeBankValues,
} from '../../Lib/Utils/FormUtils/bank-fields';
import {
  buildContactEmail,
  isFieldDisabled,
  makeSubmitButtonTextWithSignedFiles,
  showSubmitButton,
  isValidRoutingNumber,
  isValidAccountNumber,
} from '../../Lib/Utils/FormUtils/vendor-application';
import ApplicationStatus from '../../Lib/Constants/vendor-application-status';
import BankFields from '../BankFields/BankFields';
import Button from '../ReusableComponents/Button/Button';
import setupInfluencerForm from '../../Lib/Utils/FormUtils/setup-influencer-form';
import ButtonSwitch from '../VendorAttribution/ButtonSwitch';
import IndividualFields from './IndividualFields';
import CompanyFields from './CompanyFields';
import ContactInfoFields from './ContactInfoFields';
import TaxFields from './TaxFields';
import InfluencerFields from './InfluencerFields';
import SubmitApplicationChecksModal from '../ReusableComponents/Modals/SubmitApplicationChecksModal/SubmitApplicationChecksModal';
import getApplicationStatus from '../../Lib/Utils/InfluencerForm/get-application-status';
import BankFieldsUpdate from '../BankFieldsUpdate/BankFieldsUpdate';
import findMissingFields from '../../Lib/Utils/InfluencerForm/find-missing-fields';
import isDomestic from '../../Lib/Utils/VendorApplication/is-domestic';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';
import PandadocModal from './PandadocModal.tsx';
import {
  useCreateDocumentLink,
  useGetVendorDocument,
} from '../../Lib/Hooks/vendor.pandadoc.hooks.ts';
import { WxFields } from './WxFields';
import TaxIdConfirmationModal from './TaxIdConfirmationModal.tsx';
import LoadingText from '../ReusableComponents/LoadingText/LoadingText';
import {
  hasNoSpecialCharacters,
  hasNoSpecialCharactersExceptHyphens,
  getFieldDisplayName,
} from '../../Lib/Utils/FormUtils/validation';

function getWxToSign(form) {
  if (!form) {
    return 'wx';
  }
  const domestic = isDomestic(form.country);
  if (domestic) {
    return 'w9_form';
  }
  return form.is_individual ? 'w8ben_form' : 'w8bene_form';
}

const useStyles = makeStyles()(() => ({
  approvedAt: {
    padding: '15px 0px',
    textAlign: 'center',
  },
  submitButton: {
    width: '100%',
  },
}));

export default function InfluencerProfile(props) {
  const { classes, cx } = useStyles();
  const [form, setForm] = useState(setupInfluencerForm(props.vendor));
  const [emptyFields, setEmptyFields] = useState({});
  const [saved, setSaved] = useState(false);
  const [showMissingFields, setShowMissingFields] = useState(false);
  const [sameAddress, setSameAddress] = useState(false);
  const [showSubmitChecks, setShowSubmitChecks] = useState(false);
  const [showBankFieldsUpdate, setShowBankFieldsUpdate] = useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [contactInfoErrors, setContactInfoErrors] = useState(null);
  const [pandadocModalOpen, setPandadocModalOpen] = useState(false);
  const [taxFormCompleted, setTaxFormCompleted] = useState(false);
  const [showTaxIdModal, setShowTaxIdModal] = useState(false);
  const [influencerTypeSelected, setInfluencerTypeSelected] = useState(false);
  const [updateQueue, setUpdateQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bankFieldsErrors, setBankFieldsErrors] = useState(false);
  const [taxFieldsErrors, setTaxFieldsErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { mutate: createDocumentLink, data: documentLink } =
    useCreateDocumentLink();
  const taxFormType = useMemo(
    () => getWxToSign(form),
    [form.country, form.is_individual],
  );
  const { data: wxDocument, refetch: getVendorDocument } = useGetVendorDocument(
    props.user.id,
    taxFormType,
  );

  const getVendorDocumentWithLink = async () => {
    const response = await getVendorDocument();
    if (response.status === 'success') {
      createDocumentLink({
        userId: props.user.id,
        documentId: response.data.pandadoc_id,
      });
    }
  };

  useEffect(() => {
    if (taxFormType && taxFormType !== 'wx') {
      getVendorDocumentWithLink();
    }
  }, [taxFormType]);

  const wxDocumentValid = useMemo(
    () =>
      wxDocument &&
      documentLink &&
      wxDocument.status &&
      wxDocument.status.includes('completed'),
    [wxDocument, documentLink],
  );

  // This useEffect checks if the tax form needs to be completed
  useEffect(() => {
    const applicationStatus = getApplicationStatus(vendor);
    if (
      applicationStatus === ApplicationStatus.REJECTED &&
      wxDocumentValid &&
      (!updatedFields ||
        Object.keys(updatedFields).length <= 0 ||
        Object.keys(updatedFields).every(
          (value) => !WxFields[taxFormType].includes(value),
        ))
    ) {
      setTaxFormCompleted(true);
    } else if (
      applicationStatus === ApplicationStatus.REJECTED &&
      !wxDocumentValid
    ) {
      setTaxFormCompleted(false);
    }
  }, [wxDocumentValid]);

  // This useEffect set the tax form completed to true if the user has already submitted a completed tax form
  // or if the user hasn't done any changes on fields required for the tax form
  useEffect(() => {
    const applicationStatus = getApplicationStatus(vendor);
    if (
      (applicationStatus !== ApplicationStatus.APPROVED &&
        applicationStatus !== ApplicationStatus.REJECTED) ||
      (applicationStatus === ApplicationStatus.REJECTED &&
        (!wxDocumentValid ||
          (updatedFields &&
            Object.keys(updatedFields).length > 0 &&
            !Object.keys(updatedFields).every(
              (value) => !WxFields[taxFormType].includes(value),
            ))))
    ) {
      setTaxFormCompleted(false);
    } else {
      setTaxFormCompleted(true);
    }
  }, [updatedFields]);

  useEffect(() => {
    if (props.vendor.is_individual !== null) {
      setInfluencerTypeSelected(true);
    }
  }, [props.vendor]);

  function handleBeforeUnload(e) {
    const msg =
      'You have not submitted the application, are you sure you want to leave?';

    if (!saved && Object.keys(updatedFields).length > 0) {
      e.returnValue = msg;
      return msg;
    }
    const status = getApplicationStatus(props.vendor);
    if (get(props.vendor, 'submitted_at')) return undefined;

    const unsubmittedStatus = includes(
      [ApplicationStatus.APPLICATION_PENDING, ApplicationStatus.REJECTED],
      status,
    );

    if (!unsubmittedStatus) return undefined;

    e.returnValue = msg;
    return msg;
  }

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /**
   * When moving focus off of an input, applies validation to fields in real
   * time via onBlur function in props
   * @param {object} target: {
   *  @property {string} name: the name of the input being blurred,
   *  @property {string} value: the value of the input being blurred
   *    }
   */
  const onBlur = ({ target: { name, value } }) => {
    setEmptyFields((s) => ({ ...s, [name]: isEmpty(value) }));
  };

  const handleContactInfoErrors = (field, error) => {
    setContactInfoErrors((e) => ({ ...e, [field]: error }));
  };

  /**
   * Syncs the values from the main address, and copies them to the payment
   * address fields.
   */
  function syncAddressFields() {
    const address = makeAddressValues(form);
    const addressKeys = makeAddressNames();
    const payAddress = makeAddressNames(true);

    const updates = reduce(
      payAddress,
      (res, val, key) => {
        const value = address[addressKeys[key]];
        return { ...res, [val]: value };
      },
      {},
    );

    setForm((f) => ({ ...f, ...updates }));
    props.updateVendor(props.user.id, updates);
  }

  /**
   * Return true if the vendor is in update mode, else return false
   * @returns {boolean}
   */
  function isUpdate() {
    const { vendor } = props;

    const applicationStatus = getApplicationStatus(vendor);
    return (
      applicationStatus === ApplicationStatus.APPROVED ||
      applicationStatus === ApplicationStatus.REJECTED
    );
  }

  /**
   * Updates the form data in the form, checks the old form data vs the new
   * changes, and updates the vendor record.
   */
  const updateFormField = (name, value) => {
    let toNull = {};
    if (form.is_individual) {
      toNull = {
        legal_name: null,
        company_name: null,
      };
    } else {
      toNull = {
        first_name: null,
        middle_name: null,
        last_name: null,
      };
    }
    setValidationErrors((prev) => ({ ...prev, [name]: null, ...toNull }));

    const nameFields = [
      'first_name',
      'middle_name',
      'last_name',
      'legal_name',
      'company_name',
    ];

    const addressFields = [
      'address',
      'city',
      'state',
      'zip',
      'payment_billing_address',
      'payment_city',
      'payment_state',
      'payment_zip',
    ];

    const paymentFields = [
      'account_name',
      'account_number',
      'routing_number',
      'reEnterAccountNumber',
      'reEnterRoutingNumber',
      'bank_name',
    ];

    const needsValidation = [
      ...nameFields,
      ...addressFields,
      ...paymentFields,
    ].includes(name);

    if (needsValidation) {
      const isLegalName = name === 'legal_name';
      const validator = isLegalName
        ? hasNoSpecialCharactersExceptHyphens
        : hasNoSpecialCharacters;

      if (!validator(value)) {
        const errorMessage = isLegalName
          ? `${getFieldDisplayName(name)} must only contain letters, numbers, spaces, and hyphens`
          : `${getFieldDisplayName(name)} must not contain any special characters`;
        setValidationErrors((prev) => ({
          ...prev,
          [name]: errorMessage,
        }));
      }
    }

    const updates = { [name]: value };
    setForm((f) => ({ ...f, ...updates }));
    setSaved(false);

    if (isUpdate()) {
      setUpdatedFields((f) => ({ ...f, ...updates }));
    } else {
      // Add update to queue instead of sending immediately
      setUpdateQueue((queue) => [...queue, updates]);
    }
  };

  const updateAndOpenWxForm = () => {
    if (form.tax_id && form.tax_id.includes('*') && isDomestic(form.country)) {
      updateFormField('tax_id', '');
      setShowSubmitChecks(false);
      setShowTaxIdModal(true);
      return;
    }
    if (isUpdate()) {
      setPandadocModalOpen(true);
      setShowSubmitChecks(false);
      setShowTaxIdModal(false);
      return;
    }
    setPandadocModalOpen(true);
    setShowTaxIdModal(false);
    setShowSubmitChecks(false);
  };

  /**
   * Checks that the form is complete and whether it has been submitted or
   * needs to be saved for later completion.
   * @param {object?} e the event passed back when the submit button is clicked
   * on the form.
   */
  const submit = (e) => {
    if (e) e.preventDefault();

    if (Object.keys(updatedFields).length === 0 && isUpdate()) return false;

    const missingFields = findMissingFields(form, isUpdate());

    if (size(missingFields) > 0) return setShowMissingFields(true);

    setShowMissingFields(false);

    if (!taxFormCompleted) {
      return setShowSubmitChecks(true);
    }

    if (isUpdate()) {
      const applicationStatus = getApplicationStatus(vendor);
      const updates = mapValues(updatedFields, (field) =>
        field === '' ? null : field,
      );
      if (applicationStatus === ApplicationStatus.REJECTED) {
        updates.application_status = 'Pending Accounting Approval';
        updates.rejection_reason = null;
      }

      return props.updateVendorNetSuite(props.user.id, updates).then(() => {
        setSaved(true);
        setUpdatedFields({});
        setEmptyFields({});
      });
    }

    return setShowSubmitChecks(true);
  };

  /**
   * Toggles the form to use the same address for both sets of address fields
   * on the Application. Calls syncAddressFields.
   */
  function toggleSameAddress() {
    setSameAddress(!sameAddress);
    syncAddressFields();
  }

  const { currentUser, publicFilesUrls, vendor, user } = props;

  const perms = useMemo(() => {
    const canUpdateForm = hasPermissionTo(
      'users/:id/influencer-user-info',
      currentUser.role,
      Actions.UPDATE,
      'FORM',
    );
    return {
      canUpdateForm,
    };
  }, [currentUser]);

  // Process updates sequentially
  useEffect(() => {
    async function processQueue() {
      if (isProcessing || updateQueue.length === 0) return;

      setIsProcessing(true);
      const update = updateQueue[0];

      try {
        await props.updateVendor(props.user.id, update);
        setUpdateQueue((queue) => queue.slice(1));
      } catch (error) {
        console.error('Failed to process update:', error);
        setUpdateQueue((queue) => queue.slice(1));
      } finally {
        setIsProcessing(false);
      }
    }

    processQueue();
  }, [updateQueue, isProcessing, props.user.id]);

  // Add this derived state to check if any updates are in progress
  const isUpdating = updateQueue.length > 0 || isProcessing;

  // Sync payment address with company address when checkbox is checked
  useEffect(() => {
    if (sameAddress) {
      const address = makeAddressValues(form);
      const addressKeys = makeAddressNames();
      const payAddress = makeAddressNames(true);

      Object.keys(payAddress).forEach((key) => {
        const paymentField = payAddress[key];
        const value = address[addressKeys[key]];
        updateFormField(paymentField, value);
      });
    }
  }, [
    form.address,
    form.address_2,
    form.city,
    form.state,
    form.zip,
    form.country,
    sameAddress,
  ]);

  useEffect(() => {
    const domestic = isDomestic(form.payment_country);
    const bankNames = makeBankNames();

    const hasEmptyFields =
      !form[bankNames.account_name_field] ||
      !form[bankNames.bank_name_field] ||
      !form[bankNames.routing_number_field] ||
      !form[bankNames.account_number_field];

    const isRoutingValid = isValidRoutingNumber(
      form[bankNames.routing_number_field],
      domestic,
    );
    const isAccountValid = isValidAccountNumber(
      form[bankNames.account_number_field],
      domestic,
    );

    const reEnterFieldsMatch =
      form.reEnterAccountNumber === form[bankNames.account_number_field] &&
      form.reEnterRoutingNumber === form[bankNames.routing_number_field];

    setBankFieldsErrors(
      hasEmptyFields ||
        !isRoutingValid ||
        !isAccountValid ||
        !reEnterFieldsMatch,
    );
  }, [form]);

  if (!form.email) return null;
  if (isEmpty(publicFilesUrls)) return null;
  if (!vendor || isEmpty(vendor)) return null;
  if (!user || isEmpty(user)) return null;

  const { rejection_reason, internal_id } = props.vendor;
  const submittedAt = get(props.vendor, 'submitted_at', form.submitted_at);
  const domesticPayment =
    isDomestic(form.payment_country) || !form.payment_country;
  const domesticCompany = isDomestic(form.country) || !form.country;
  const applicationStatus = getApplicationStatus(props.vendor);
  const hasContactInfoErrors = chain(contactInfoErrors)
    .keys()
    .map((key) => contactInfoErrors[key])
    .some((error) => error !== null)
    .value();

  const hasValidationErrors =
    Object.values(validationErrors).filter((f) => f !== null).length > 0;

  // TODO: [HALO-MIGRATION] Remove isMigratedToHalo and all its usages after migration is complete
  const isMigratedToHalo =
    props.currentUser?.migrated_to_halo === 'influencer' ||
    props.currentUser?.migrated_to_halo === 'agency';

  return (
    <Page.Container withoutTwp>
      {showSubmitChecks && (
        <SubmitApplicationChecksModal
          form={form}
          onClose={() => setShowSubmitChecks(false)}
          open={showSubmitChecks}
          submitVendor={
            !taxFormCompleted ? updateAndOpenWxForm : props.createVendorNetSuite
          }
          beforeSign={!taxFormCompleted}
          user={props.user}
          validateBIC={props.validateBIC}
          validateIBAN={props.validateIBAN}
          validateTIN={props.validateTIN}
        />
      )}
      <Header.Major icon="safe" title="Payee Information" />
      <Page.Content>
        <form onSubmit={submit}>
          <h2 className={styles.profileHeader}>Tax and Payment Information</h2>
          <div className={styles.statusRow}>
            <h3
              className={cx({
                [styles.halfRow]: !!rejection_reason,
                [styles.halfField]: !rejection_reason,
              })}
            >
              <span>Status: </span>
              <span className={styles.applicationStatus}>
                {startCase(applicationStatus)}
              </span>
            </h3>
          </div>
          {(!internal_id && !rejection_reason && !form.submitted_at) ||
          applicationStatus === ApplicationStatus.ARCHIVE ? (
            <span>
              <p className={styles.vendorSupportContact}>
                {`If you are an existing Influencer with Fashion Nova and the status
              above shows APPLICATION PENDING OR ARCHIVE, please contact `}
                <a href={`${buildContactEmail(currentUser)}`}>
                  influencerdocs@fashionnova.com
                </a>
                {` to gain access to your account.`}
              </p>
            </span>
          ) : null}
          {rejection_reason &&
            applicationStatus === ApplicationStatus.REJECTED && (
              <FormControl variant="standard" margin="dense" fullWidth>
                <div
                  id="va-rejection-reason"
                  className={styles.rejectionReason}
                >
                  {`Rejection Reason - ${startCase(rejection_reason)}`}
                </div>
              </FormControl>
            )}
          <br />
          <Prompt
            when={
              submittedAt && !saved && Object.keys(updatedFields).length > 0
            }
            message="Your changes have not been saved, do you want to quit anyway?"
          />
          <If condition={taxFormType !== 'wx' && vendor[taxFormType].exists}>
            <a
              target="_blank"
              href={vendor[taxFormType].read}
              className="twp text-primary-700 font-semibold text-sm"
              rel="noopener noreferrer"
            >
              View Signed {taxFormType.includes('w9') ? 'W9' : 'W8'}
            </a>
          </If>
          <div className={styles.row}>
            <div>
              <InputLabel htmlFor="influencer-individual-or-company">
                Are we paying you as an Individual or a Company?
              </InputLabel>
              <ButtonSwitch
                selected={!influencerTypeSelected ? null : !!form.is_individual}
                left="individual"
                leftLabel="Individual"
                right="company"
                rightLabel="Company"
                id="influencer-individual-or-company"
                onChange={(v) => {
                  setInfluencerTypeSelected(true);
                  const updates = {
                    is_individual: v,
                    company_name: null,
                    legal_name: null,
                    tax_classification: v ? 'Individual/Sole Proprietor' : null,
                  };
                  setForm((f) => ({ ...f, ...updates }));
                  setSaved(false);
                  if (isUpdate()) {
                    setUpdatedFields((f) => ({ ...f, ...updates }));
                  } else {
                    props.updateVendor(props.user.id, updates);
                  }
                }}
                disabled={
                  isFieldDisabled(applicationStatus) ||
                  !perms.canUpdateForm ||
                  isMigratedToHalo
                }
              />
            </div>
          </div>
          {influencerTypeSelected && (
            <>
              <h4 className={styles.header}>Payee Information</h4>
              <h4 className={styles.subHeader}>Legal Name</h4>
              <Feedback
                title={
                  form.is_individual
                    ? 'You must enter your full legal name (first, middle, and last).'
                    : 'Enter your registered legal name and Company DBA name if different than registered legal name.'
                }
                description="This is required for legal and tax purposes. Incomplete names will result in your application being rejected."
                type="info"
                className="mb-5"
              />
              {form.is_individual ? (
                <IndividualFields
                  form={form}
                  onChange={updateFormField}
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdateForm ||
                    isMigratedToHalo
                  }
                  validationErrors={validationErrors}
                />
              ) : (
                <CompanyFields
                  form={form}
                  onChange={updateFormField}
                  domestic={domesticCompany}
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdateForm ||
                    isMigratedToHalo
                  }
                  validationErrors={validationErrors}
                />
              )}
              <br />
              <h4 className={styles.subHeader}>CONTACT INFO</h4>
              <ContactInfoFields
                form={form}
                onChange={updateFormField}
                onErrorUpdate={handleContactInfoErrors}
                disabled={
                  isFieldDisabled(applicationStatus) ||
                  !perms.canUpdateForm ||
                  isMigratedToHalo
                }
              />
              <h4 className={styles.header}>Address</h4>
              <AddressFields
                values={makeAddressValues(form)}
                names={makeAddressNames()}
                domestic={domesticCompany}
                prefix="va-company"
                onChange={updateFormField}
                onBlur={onBlur}
                emptyFields={emptyFields}
                disabled={
                  (isFieldDisabled(applicationStatus) &&
                    applicationStatus !== ApplicationStatus.APPROVED) ||
                  !perms.canUpdateForm ||
                  isMigratedToHalo
                }
                required
                validationErrors={validationErrors}
              />
              <h4 className={styles.header}>Tax Information</h4>
              {form.country ? (
                <div>
                  <TaxFields
                    disabled={
                      isFieldDisabled(applicationStatus) ||
                      !perms.canUpdateForm ||
                      isMigratedToHalo
                    }
                    domestic={domesticCompany}
                    form={form}
                    onChange={updateFormField}
                    setTaxFieldsErrors={setTaxFieldsErrors}
                  />
                  <br />
                </div>
              ) : (
                <Feedback
                  title="Please select a country to complete your Tax information."
                  type="info"
                  className="mb-5"
                  data-testid="feedback-payment-information"
                />
              )}
              <h4 className={styles.header}>Payment Information</h4>
              <Feedback
                title={
                  <>
                    Make sure your bank accepts{' '}
                    <span className="font-black">USD</span> payments to avoid
                    delays or rejections.
                  </>
                }
                type="info"
                className="mb-5"
                data-testid="usd-payment-feedback"
              />
              <h4>
                <span className={styles.subHeader}>BILLING ADDRESS</span>
                {!submittedAt && !internal_id && (
                  <label
                    className={styles.sameAddress}
                    htmlFor="same-address-checkbox"
                  >
                    <input
                      id="same-address-checkbox"
                      type="checkbox"
                      checked={sameAddress}
                      onChange={toggleSameAddress}
                      disabled={!perms.canUpdateForm || isMigratedToHalo}
                    />
                    same as company address
                  </label>
                )}
              </h4>
              {!sameAddress && (
                <AddressFields
                  values={makeAddressValues(form, true)}
                  names={makeAddressNames(true)}
                  prefix="va-payment"
                  domestic={domesticPayment}
                  onChange={updateFormField}
                  onBlur={onBlur}
                  emptyFields={emptyFields}
                  required
                  disabled={
                    isFieldDisabled(applicationStatus) ||
                    !perms.canUpdateForm ||
                    isMigratedToHalo
                  }
                  validationErrors={validationErrors}
                />
              )}
              <br />
              <br />
              <BankFieldsUpdate
                emptyFields={emptyFields}
                open={showBankFieldsUpdate}
                vendor={props.vendor}
                values={{
                  ...pick(form, [
                    'payment_billing_address',
                    'payment_billing_address_2',
                    'payment_country',
                    'payment_state',
                    'payment_city',
                    'payment_zip',
                  ]),
                }}
                names={makeBankNames()}
                onClose={() => setShowBankFieldsUpdate(false)}
                onSave={(updates) => {
                  setForm((f) => ({ ...f, ...updates }));

                  props.updateVendor(props.user.id, updates).then(() => {
                    props
                      .updateVendorNetSuite(props.user.id, updates)
                      .then(() => {
                        setSaved(true);
                        setEmptyFields({});
                        setShowBankFieldsUpdate(false);
                      });
                  });
                }}
              />
              <BankFields
                values={makeBankValues(form)}
                names={makeBankNames()}
                prefix="va"
                onChange={updateFormField}
                showUpdateButton={
                  props.currentUser.role === 'INFLUENCER' &&
                  applicationStatus === ApplicationStatus.APPROVED &&
                  perms.canUpdateForm &&
                  !isMigratedToHalo
                }
                onUpdateButtonClick={() => setShowBankFieldsUpdate(true)}
                onBlur={onBlur}
                emptyFields={emptyFields}
                disabled={
                  isFieldDisabled(applicationStatus) ||
                  !perms.canUpdateForm ||
                  isMigratedToHalo
                }
                domestic={domesticPayment}
                header="Payment Info"
                required
                isInfluencer
                validationErrors={validationErrors}
              />
              <br />
              <h4 className={styles.header}>Contact Information</h4>
              <h4 className={styles.subHeader}>Influencer Details</h4>
              <InfluencerFields
                form={form}
                onChange={updateFormField}
                disabled={
                  (isFieldDisabled(applicationStatus) &&
                    applicationStatus !== ApplicationStatus.APPROVED) ||
                  !perms.canUpdateForm ||
                  isMigratedToHalo
                }
              />
              <br />
              <div className={styles.buttonContainer}>
                {applicationStatus === ApplicationStatus.APPROVED &&
                  !isMigratedToHalo && (
                    <Button
                      type="submit"
                      className={styles.saveButton}
                      variant="contained"
                      onClick={submit}
                      color="primary"
                      disabled={!perms.canUpdateForm || hasValidationErrors}
                    >
                      {saved ? 'Saved!' : 'Save'}
                    </Button>
                  )}

                {showSubmitButton(internal_id, rejection_reason) &&
                  !isMigratedToHalo && (
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      onClick={submit}
                      disabled={
                        isFieldDisabled(applicationStatus) ||
                        hasContactInfoErrors ||
                        !perms.canUpdateForm ||
                        isUpdating ||
                        bankFieldsErrors ||
                        taxFieldsErrors ||
                        hasValidationErrors
                      }
                    >
                      {isUpdating ? (
                        <LoadingText text="Saving" />
                      ) : (
                        makeSubmitButtonTextWithSignedFiles(
                          applicationStatus,
                          taxFormCompleted,
                        )
                      )}
                    </Button>
                  )}
                {showMissingFields &&
                  size(findMissingFields(form, isUpdate())) > 0 && (
                    <Grid container style={{ color: 'red', marginTop: '15px' }}>
                      In order to {!taxFormCompleted ? 'continue' : 'submit'}{' '}
                      please make sure to fill out the following fields: {` `}
                      {chain(findMissingFields(form, isUpdate()))
                        .map(startCase)
                        .map((value) => {
                          const legalNameLabel = domesticCompany
                            ? 'Registered Legal Name'
                            : 'Name of Organization';
                          return value === 'Legal Name'
                            ? legalNameLabel
                            : value;
                        })
                        .join(', ')
                        .value()}
                    </Grid>
                  )}
                {hasContactInfoErrors && (
                  <Grid container style={{ color: 'red', marginTop: '15px' }}>
                    In order to submit please make fix the error on the
                    following fields: {` `}
                    {Object.keys(contactInfoErrors)
                      .filter((key) => contactInfoErrors[key] !== null)
                      .join(', ')}
                  </Grid>
                )}
                {hasValidationErrors && (
                  <Grid container style={{ color: 'red', marginTop: '15px' }}>
                    Please fix the following validation errors:
                    <ul style={{ margin: '5px 0 0 20px' }}>
                      {Object.entries(validationErrors)
                        .filter(([_, error]) => error !== null)
                        .map(([field, error]) => (
                          <li key={field}>{error}</li>
                        ))}
                    </ul>
                  </Grid>
                )}
              </div>
              {props.vendor.approved_at && (
                <div className={styles.documentContainer}>
                  <p className={classes.approvedAt}>
                    Application Submitted on
                    {` ${moment(props.vendor.approved_at).format('LLLL')}`}
                  </p>
                </div>
              )}
            </>
          )}
        </form>
      </Page.Content>
      <TaxIdConfirmationModal
        open={showTaxIdModal}
        onClose={() => setShowTaxIdModal(false)}
        onConfirm={() => {
          updateAndOpenWxForm();
          setShowTaxIdModal(false);
        }}
        value={form.tax_id}
        onChangeValue={(value) => updateFormField('tax_id', value)}
        validateTIN={async () => {
          const args = { tin: form.tax_id, lastName: form.last_name };
          return props.validateTIN(args);
        }}
      />
      <PandadocModal
        form={form}
        userId={props.user.id}
        individual={form.is_individual}
        documentName={taxFormType}
        open={pandadocModalOpen}
        onClose={() => setPandadocModalOpen(false)}
        onCompletion={async () => {
          await getVendorDocumentWithLink();
          const appStatus = getApplicationStatus(vendor);
          const updates = { file: taxFormType, ...updatedFields };
          if (appStatus === ApplicationStatus.REJECTED) {
            updates.application_status = 'Pending Accounting Approval';
            updates.rejection_reason = null;
          }
          return props.updateVendorNetSuite(props.user.id, updates).then(() => {
            setTaxFormCompleted(true);
            setPandadocModalOpen(false);
            setShowSubmitChecks(true);
          });
        }}
      />
    </Page.Container>
  );
}

InfluencerProfile.propTypes = {
  updateVendor: PropTypes.func.isRequired,
  updateVendorNetSuite: PropTypes.func.isRequired,
  createVendorNetSuite: PropTypes.func.isRequired,
  vendor: PropTypes.shape({
    id: PropTypes.string,
    internal_id: PropTypes.string,
    approved_at: PropTypes.string,
    submitted_at: PropTypes.string,
    rejection_reason: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({ id: PropTypes.number }).isRequired,
  publicFilesUrls: PropTypes.shape({}).isRequired,
  validateBIC: PropTypes.func.isRequired,
  validateTIN: PropTypes.func.isRequired,
  validateIBAN: PropTypes.func.isRequired,
};
